import { isNonNil } from './object'

const HA_PER_AC = 0.404686
const AC_PER_HA = 1 / HA_PER_AC
const METRIC_TONNES_PER_TON = 0.907185
const LBS_PER_KG = 2.20462
const KG_PER_LBS = 1 / LBS_PER_KG
const GAL_PER_L = 0.264172
const L_PER_GAL = 1 / GAL_PER_L
const CM_PER_IN = 2.54

export const unitConversions = {
  // Weight conversions
  'lbs/ac': {
    metricLabel: 'kg/ha',
    conversion: (v) => v * KG_PER_LBS * AC_PER_HA
  },
  'lbs/min': {
    metricLabel: 'kg/min',
    conversion: (v) => v * KG_PER_LBS
  },
  'lbs/hr': {
    metricLabel: 'kg/hr',
    conversion: (v) => v * KG_PER_LBS
  },
  'lbs/sec': {
    metricLabel: 'kg/sec',
    conversion: (v) => v * KG_PER_LBS
  },
  'lbs/rev/row': {
    metricLabel: 'kg/rev/row',
    conversion: (v) => v * KG_PER_LBS
  },
  lbs: {
    metricLabel: 'kg',
    conversion: (v) => v * KG_PER_LBS
  },
  oz: {
    metricLabel: 'g',
    conversion: (v) => v * 28.3495
  },

  // Length conversions
  in: {
    metricLabel: 'cm',
    conversion: (v) => v * CM_PER_IN
  },
  'in H20': {
    metricLabel: 'cm H20',
    conversion: (v) => v * CM_PER_IN
  },
  ft: {
    metricLabel: 'm',
    conversion: (v) => v * 0.3048
  },

  // Volume conversions
  'gal/ac': {
    metricLabel: 'L/ha',
    conversion: (v) => v * L_PER_GAL * AC_PER_HA
  },
  gal: {
    metricLabel: 'L',
    conversion: (v) => v * L_PER_GAL
  },
  'gal/min': {
    metricLabel: 'L/min',
    conversion: (v) => v * L_PER_GAL
  },
  'gal/rev': {
    metricLabel: 'L/rev',
    conversion: (v) => v * L_PER_GAL
  },

  // Flow meter calibration conversions
  'pulses/gal': {
    metricLabel: 'pulses/L',
    conversion: (v) => v * GAL_PER_L
  },

  // Area conversions
  ac: {
    metricLabel: 'ha',
    conversion: (v) => v * HA_PER_AC
  },
  Acres: {
    metricLabel: 'Hectares',
    conversion: (v) => v * HA_PER_AC
  },
  'ac/hr': {
    metricLabel: 'ha/hr',
    conversion: (v) => v * HA_PER_AC
  },

  // Pressure conversions
  psi: {
    metricLabel: 'kPa',
    conversion: (v) => v * 6.89476
  },

  // Temperature conversions
  '°F': {
    metricLabel: '°C',
    conversion: (v) => ((v - 32) * 5) / 9
  },

  // Speed conversions
  mph: {
    metricLabel: 'km/h',
    conversion: (v) => v * 1.60934
  },

  // Force conversions
  G: {
    metricLabel: 'm/sec/sec',
    conversion: (v) => v * 9.80665
  },

  // Seed count conversions
  'seeds/ac': {
    metricLabel: 'seeds/ha',
    conversion: (v) => v * AC_PER_HA
  },

  // tons
  tons: {
    metricLabel: 'tonnes',
    conversion: (v) => v * METRIC_TONNES_PER_TON
  },
  'tons/ac': {
    metricLabel: 'tonnes/ha',
    conversion: (v) => v * METRIC_TONNES_PER_TON * AC_PER_HA
  },

  // sacks
  'sacks/ac': {
    metricLabel: 'sacks/ha',
    conversion: (v) => v * AC_PER_HA
  },

  // Yield conversions
  bu: {
    metricLabel: 'bu',
    conversion: (v) => v
  },
  'bu/ac': {
    metricLabel: 'bu/ha',
    conversion: (v) => v * AC_PER_HA
  }
} as const satisfies Record<string, { metricLabel: string; conversion: (value: number) => number }>

export const metricToImperial = {
  'kg/ha': 'lbs/ac',
  'kg/min': 'lbs/min',
  'kg/hr': 'lbs/hr',
  'kg/sec': 'lbs/sec',
  'kg/rev/row': 'lbs/rev/row',
  kg: 'lbs',
  g: 'oz',
  cm: 'in',
  'cm H20': 'in H20',
  m: 'ft',
  'L/ha': 'gal/ac',
  L: 'gal',
  'L/min': 'gal/min',
  'L/rev': 'gal/rev',
  ha: 'ac',
  Hectares: 'Acres',
  'ha/hr': 'ac/hr',
  kPa: 'psi',
  '°C': '°F',
  'km/h': 'mph',
  'm/sec/sec': 'G',
  'pulses/L': 'pulses/gal',
  'seeds/ha': 'seeds/ac',
  tonnes: 'tons',
  'tonnes/ha': 'tons/ac',
  'sacks/ha': 'sacks/ac',
  bu: 'bu',
  'bu/ha': 'bu/ac'
} as Record<string, string>

type Unit = keyof typeof unitConversions

const isConvertibleUnit = (unit?: string): unit is Unit => isNonNil(unit) && unit in unitConversions

/**
 * Convert a value from an imperial unit to its metric equivalent.
 */
export const convertFromImperial = (value: number, unit: string): number => {
  if (!isConvertibleUnit(unit)) return value

  const { conversion } = unitConversions[unit]

  return conversion(value)
}

export const convertToImperial = (value: number, metricUnit: string): number => {
  const unit = metricToImperial[metricUnit]

  if (!isConvertibleUnit(unit)) return value

  const { conversion } = unitConversions[unit]

  if (unit === '°F') {
    return (value * 9) / 5 + 32
  }

  return value / conversion(1)
}
/**
 * Retrieve the metric label for a given unit.
 */
export const nameForMetricUnit = (unit?: string): string => {
  if (!isConvertibleUnit(unit)) return unit ?? ''

  const { metricLabel } = unitConversions[unit]

  return metricLabel
}
